import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { 
  ProfessionalTemplate, 
  CreativeTemplate, 
  MinimalistTemplate,
  ExecutiveTemplate,
  ModernTemplate,
  ElegantTemplate,
  TechnicalTemplate,
  AcademicTemplate,
  StartupTemplate
} from './ResumeTemplates';
import { Download, Eye, Share2, Award, CheckCircle, Star, Upload, Camera, X, User } from 'lucide-react';

const PhotoUploadSection = ({ photo, onPhotoChange, onPhotoRemove }) => {
  const fileInputRef = useRef(null);
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    console.log('📸 File selected:', file ? file.name : 'No file');
    
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select an image file (PNG, JPG, JPEG)');
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Please select an image smaller than 5MB');
        // Clear the file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        return;
      }
      
      console.log('📸 Converting file to base64...');
      // Convert to base64
      const reader = new FileReader();
      reader.onload = (e) => {
        console.log('📸 Base64 conversion complete');
        onPhotoChange(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handlePhotoRemoveClick = () => {
    console.log('📸 Removing photo and clearing file input');
    // Clear the file input to allow re-uploading the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onPhotoRemove();
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm border">
      <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
        <Camera className="w-5 h-5 mr-2" />
        Add Your Photo (Optional)
      </h3>
      <p className="text-gray-600 mb-4">
        Adding a professional photo can make your resume more personal and memorable.
      </p>
      
      <div className="flex items-center space-x-6">
        {/* Photo Preview */}
        <div className="relative">
          {photo ? (
            <div className="relative group">
              <img 
                src={photo} 
                alt="Profile" 
                className="w-24 h-24 rounded-full object-cover border-4 border-gray-200"
              />
              <button
                onClick={handlePhotoRemoveClick}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <div className="w-24 h-24 rounded-full bg-gray-100 border-4 border-gray-200 flex items-center justify-center">
              <User className="w-8 h-8 text-gray-400" />
            </div>
          )}
        </div>
        
        {/* Upload Button */}
        <div className="flex-1">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/jpg"
            onChange={handleFileChange}
            className="hidden"
            id="photo-upload"
          />
          <label
            htmlFor="photo-upload"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            {photo ? 'Change Photo' : 'Upload Photo'}
          </label>
          {photo && (
            <button
              onClick={handlePhotoRemoveClick}
              className="ml-3 inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <X className="w-4 h-4 mr-2" />
              Remove Photo
            </button>
          )}
          <p className="text-sm text-gray-500 mt-2">
            Recommended: Professional headshot, PNG/JPG, max 5MB
          </p>
        </div>
      </div>
    </div>
  );
};

const TemplateViewer = ({ resumeData, selectedTemplate, onTemplateChange }) => {
  const templates = {
    professional: ProfessionalTemplate,
    creative: CreativeTemplate,
    minimalist: MinimalistTemplate,
    executive: ExecutiveTemplate,
    modern: ModernTemplate,
    elegant: ElegantTemplate,
    technical: TechnicalTemplate,
    academic: AcademicTemplate,
    startup: StartupTemplate
  };

  const TemplateComponent = templates[selectedTemplate] || ProfessionalTemplate;

  // Debug: Log the template rendering
  console.log('🎨 TemplateViewer rendering:', { selectedTemplate, resumeData });
  console.log('🎨 Selected TemplateComponent:', TemplateComponent.name);

  return (
    <div className="bg-white rounded-xl shadow-lg p-8">
      <div className="mb-4 text-sm text-gray-500">
        Rendering: {selectedTemplate} template
      </div>
      <TemplateComponent resumeData={resumeData} />
    </div>
  );
};

const ResumeTemplateSelector = ({ selectedTemplate, onTemplateSelect }) => {
  const templates = [
    { 
      id: 'professional', 
      name: 'Professional', 
      description: 'Clean corporate design perfect for traditional industries',
      preview: '📄',
      gradient: 'from-blue-500 to-blue-700'
    },
    { 
      id: 'creative', 
      name: 'Creative', 
      description: 'Modern design with visual flair for creative roles',
      preview: '🎨',
      gradient: 'from-purple-500 to-pink-500'
    },
    { 
      id: 'minimalist', 
      name: 'Minimalist', 
      description: 'Clean and simple layout focusing on content',
      preview: '✨',
      gradient: 'from-gray-500 to-gray-700'
    },
    { 
      id: 'executive', 
      name: 'Executive', 
      description: 'Bold leadership-focused design for senior roles',
      preview: '👔',
      gradient: 'from-gray-800 to-black'
    },
    { 
      id: 'modern', 
      name: 'Modern', 
      description: 'Contemporary gradient design for tech professionals',
      preview: '🚀',
      gradient: 'from-indigo-500 to-pink-500'
    },
    { 
      id: 'elegant', 
      name: 'Elegant', 
      description: 'Sophisticated and refined for premium roles',
      preview: '🌸',
      gradient: 'from-rose-500 to-amber-500'
    },
    { 
      id: 'technical', 
      name: 'Technical', 
      description: 'Developer-focused terminal style design',
      preview: '💻',
      gradient: 'from-green-500 to-green-700'
    },
    { 
      id: 'academic', 
      name: 'Academic', 
      description: 'Scholarly and professional for education sector',
      preview: '🎓',
      gradient: 'from-blue-800 to-blue-900'
    },
    { 
      id: 'startup', 
      name: 'Startup', 
      description: 'Energetic entrepreneur design for innovators',
      preview: '💡',
      gradient: 'from-yellow-500 to-orange-500'
    }
  ];

  return (
    <div className="space-y-4">
      <h3 className="text-xl font-bold text-gray-900">Choose Your Template</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {templates.map((template) => (
          <motion.div
            key={template.id}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onTemplateSelect(template.id)}
            className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
              selectedTemplate === template.id
                ? 'border-blue-500 bg-blue-50 shadow-lg'
                : 'border-gray-200 hover:border-gray-300 hover:shadow-md'
            }`}
          >
            <div className={`w-12 h-12 bg-gradient-to-r ${template.gradient} rounded-lg flex items-center justify-center text-white text-xl mb-3`}>
              {template.preview}
            </div>
            <h4 className="font-semibold text-gray-900 mb-1">{template.name}</h4>
            <p className="text-sm text-gray-600 leading-relaxed">{template.description}</p>
            
            {selectedTemplate === template.id && (
              <div className="mt-3 flex items-center text-blue-600 text-sm font-medium">
                <CheckCircle className="w-4 h-4 mr-1" />
                Selected
              </div>
            )}
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const OptimizedResumeResults = ({ fullResult, onDownload }) => {
  const [selectedTemplate, setSelectedTemplate] = useState('professional');
  const [activeView, setActiveView] = useState('template'); // 'template' or 'analysis'
  // Initialize photo state from fullResult.photo if available
  const [photo, setPhoto] = useState(fullResult?.photo || null); 

  // Browser navigation warning
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = '⚠️ Your optimized resume will be lost! Please download your resume before leaving this page.';
      return '⚠️ Your optimized resume will be lost! Please download your resume before leaving this page.';
    };

    const handlePopState = (e) => {
      const shouldLeave = window.confirm(
        '⚠️ Your optimized resume will be lost!\n\nDownload your resume before going back, or you\'ll need to start fresh.\n\nAre you sure you want to leave?'
      );
      
      if (!shouldLeave) {
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href);
        e.preventDefault();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    
    // Push a state to handle back button
    window.history.pushState(null, '', window.location.href);

    // Cleanup
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Debug: Log the fullResult to see what data we're getting
  console.log('🔍 OptimizedResumeResults - fullResult:', fullResult);
  console.log('🔍 structured_resume_data:', fullResult?.structured_resume_data);

  const handleDownload = (format) => {
    // Include photo in the download data
    const resumeDataWithPhoto = photo ? {
      ...fullResult?.structured_resume_data,
      photo: photo
    } : fullResult?.structured_resume_data;
    
    // Pass the enhanced data to the download function
    onDownload(format, selectedTemplate, resumeDataWithPhoto);
  };

  const handlePhotoChange = (photoData) => {
    setPhoto(photoData);
  };

  const handlePhotoRemove = () => {
    setPhoto(null);
  };

  // Create fallback data if structured_resume_data is missing
  const resumeData = {
    ...(fullResult?.structured_resume_data || {
      name: "Resume data not available",
      title: "Please check console for debugging info",
      email: "debug@example.com",
      phone: "(000) 000-0000",
      location: "Debug Mode",
      summary: `Debug: fullResult keys: ${Object.keys(fullResult || {}).join(', ')}`,
      skills: ["Debug", "Troubleshooting", "Data Missing"],
      experience: [{
        title: "Debug Information",
        company: "System Check",
        period: "Now",
        achievements: ["Checking data flow", "Debugging template", "Finding missing data"]
      }],
      education: {
        degree: "Debug Mode",
        school: "System Check",
        year: "2024"
      }
    }),
    photo: photo // Include the uploaded photo
  };

  return (
    <div className="space-y-8">
      {/* Success Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center space-y-4"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
          <Award className="w-8 h-8 text-green-600" />
        </div>
        <h2 className="text-3xl font-bold text-gray-900">Resume Optimized Successfully!</h2>
        <p className="text-lg text-gray-600">Your resume has been enhanced with AI-powered optimization</p>
      </motion.div>

      {/* ATS Score Display */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2 }}
        className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-6 text-center"
      >
        <div className="flex items-center justify-center space-x-8">
          <div>
            <div className="text-4xl font-bold text-green-600">{fullResult.ats_score}%</div>
            <div className="text-sm text-gray-600">ATS Score</div>
          </div>
          <div className="text-gray-300">→</div>
          <div>
            <div className="text-sm text-gray-600 mb-1">Improvement</div>
            <div className="text-2xl font-bold text-blue-600">
              +{fullResult.ats_score - (fullResult.original_score || 30)}%
            </div>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-center space-x-6 text-sm text-gray-600">
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            Optimized for ATS systems
          </div>
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-500 mr-1" />
            Professional templates
          </div>
          <div className="flex items-center">
            <Download className="w-4 h-4 text-blue-500 mr-1" />
            Instant download
          </div>
        </div>
      </motion.div>

      {/* View Toggle */}
      <div className="flex justify-center">
        <div className="bg-gray-100 rounded-lg p-1 flex">
          <button
            onClick={() => setActiveView('template')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              activeView === 'template'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye className="w-4 h-4 inline mr-2" />
            Templates
          </button>
          <button
            onClick={() => setActiveView('analysis')}
            className={`px-6 py-2 rounded-md font-medium transition-all ${
              activeView === 'analysis'
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Award className="w-4 h-4 inline mr-2" />
            Analysis
          </button>
        </div>
      </div>

      {activeView === 'template' ? (
        <div className="space-y-8">
          {/* Photo Upload Section */}
          <PhotoUploadSection
            photo={photo}
            onPhotoChange={handlePhotoChange}
            onPhotoRemove={handlePhotoRemove}
          />

          {/* Template Selector */}
          <ResumeTemplateSelector
            selectedTemplate={selectedTemplate}
            onTemplateSelect={setSelectedTemplate}
          />

          {/* Template Preview */}
          <motion.div
            key={selectedTemplate}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <TemplateViewer
              resumeData={resumeData}
              selectedTemplate={selectedTemplate}
              onTemplateChange={setSelectedTemplate}
            />
          </motion.div>

          {/* Download Options */}
          <div className="bg-gray-50 rounded-xl p-6 text-center space-y-4">
            <h3 className="text-xl font-bold text-gray-900">Download Your Resume</h3>
            <div className="flex justify-center space-x-4">
              <button
                onClick={() => handleDownload('pdf')}
                className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                PDF
              </button>
              <button
                onClick={() => handleDownload('docx')}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                DOCX
              </button>
              <button
                onClick={() => handleDownload('txt')}
                className="bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
              >
                <Download className="w-4 h-4 mr-2" />
                TXT
              </button>
            </div>
          </div>
        </div>
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-6"
        >
          {/* Optimization Summary */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Optimization Summary</h3>
            <p className="text-gray-700 leading-relaxed">{fullResult.optimization_summary}</p>
          </div>

          {/* Improvements */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Key Improvements</h3>
            <div className="space-y-3">
              {fullResult.improvements?.map((improvement, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                  <span className="text-gray-700">{improvement}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Keyword Matches */}
          <div className="bg-white rounded-xl p-6 shadow-sm border">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Keywords Added</h3>
            <div className="flex flex-wrap gap-2">
              {fullResult.keyword_matches?.map((keyword, index) => (
                <span
                  key={index}
                  className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default OptimizedResumeResults;
